import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Message } from '@/interfaces/message'
import { Ticket } from '@/interfaces/ticket'
import { cn } from '@/lib/utils'
import {
  listAgentTicketsQuery,
  listDeskTicketsQuery,
  listFinishedTicketsQuery,
  listPendingTicketsQuery,
} from '@/queries/ticket.query'
import {
  ChatsTabType,
  selectActiveTab,
  selectCurrentTicketId,
  selectFocusTicket,
  selectSetActiveTab,
  selectSetDialog,
  selectUnreadMessages,
  useChatStore,
} from '@/stores/chat.store'
import { avatarName, getAvatarColor } from '@/utils/avatar-name'
import {
  IconBrain,
  IconCheck,
  IconClock,
  IconEdit,
  IconMessageCircle,
  IconMessages,
  IconSearch,
} from '@tabler/icons-react'
import { useSuspenseQuery } from '@tanstack/react-query'
import {
  AudioLines,
  FileText,
  Image,
  LayoutTemplate,
  MapPin,
  Sticker,
  Video,
} from 'lucide-react'
import { Fragment, useCallback, useRef, useState } from 'react'

function getLastMessageContent(message?: Message | null) {
  if (!message) return ''
  if (message.type === 'reaction') return `Reagiu com ${message.content}`
  if (message.type === 'text') return message.content
  if (message.type === 'image')
    return (
      <div className='flex gap-1'>
        <Image size={16} />
        <span className='text-xs text-muted-foreground'>{message.caption}</span>
      </div>
    )
  if (message.type === 'audio' || message.type === 'ptt')
    return (
      <div className='flex gap-1'>
        <AudioLines size={16} />
        <span className='text-xs text-muted-foreground'>{message.caption}</span>
      </div>
    )
  if (message.type === 'video')
    return (
      <div className='flex gap-1'>
        <Video size={16} />
        <span className='text-xs text-muted-foreground'>{message.caption}</span>
      </div>
    )
  if (message.type === 'document')
    return (
      <div className='flex gap-1'>
        <FileText size={16} />
        <span className='text-xs text-muted-foreground'>
          {message.filename || 'Documento'}
        </span>
      </div>
    )
  if (message.type === 'sticker')
    return (
      <div className='flex items-center gap-1 text-xs text-muted-foreground'>
        <Sticker size={16} />
        <span>Sticker</span>
      </div>
    )
  if (message.type === 'template')
    return (
      <div className='flex items-center gap-1 text-xs text-muted-foreground'>
        <LayoutTemplate size={16} />
        <span className='truncate'>{message.content || 'Template'}</span>
      </div>
    )
  if (message.type === 'location') return <MapPin size={16} />
  return ''
}

const emptyStateConfig = {
  atendendo: {
    icon: IconMessageCircle,
    title: 'Nenhum atendimento em andamento',
    description: 'Você não está atendendo nenhum chat no momento.',
  },
  agente: {
    icon: IconBrain,
    title: 'Nenhum atendimento do agente',
    description: 'Não há tickets ativos sendo respondidos pelo agente.',
  },
  esperando: {
    icon: IconClock,
    title: 'Nenhum chat em espera',
    description: 'Não há chats aguardando atendimento no momento.',
  },
  finalizados: {
    icon: IconCheck,
    title: 'Nenhum atendimento finalizado',
    description: 'Ainda não há atendimentos finalizados.',
  },
}

export function ChatSidebar() {
  const [search, setSearch] = useState('')
  const setOpen = useChatStore(selectSetDialog)
  const activeTab = useChatStore(selectActiveTab)
  const setActiveTab = useChatStore(selectSetActiveTab)
  const currentTicketId = useChatStore(selectCurrentTicketId)
  const focusTicket = useChatStore(selectFocusTicket)
  const unreadMessages = useChatStore(selectUnreadMessages)
  const { data: atendendoChats } = useSuspenseQuery(listDeskTicketsQuery)
  const { data: agenteChats } = useSuspenseQuery(listAgentTicketsQuery)
  const { data: esperandoChats } = useSuspenseQuery(listPendingTicketsQuery)
  const { data: finalizadosChats } = useSuspenseQuery(listFinishedTicketsQuery)
  const sidebarRef = useRef<HTMLDivElement>(null)

  const filterTickets = useCallback(
    (tickets: Ticket[]) => {
      if (!search.trim()) return tickets

      const searchLower = search.toLowerCase().trim()
      return tickets.filter((ticket) => {
        const nameMatch = ticket.contact.name
          .toLowerCase()
          .includes(searchLower)
        const phoneMatch = ticket.contact.phone.includes(searchLower)
        const messageMatch =
          ticket.lastMessage?.type === 'text' &&
          ticket.lastMessage?.content?.toLowerCase().includes(searchLower)

        return nameMatch || phoneMatch || messageMatch
      })
    },
    [search]
  )

  const renderChatList = useCallback(
    (tickets: Ticket[], tab: ChatsTabType) => {
      if (tickets.length === 0) {
        if (search.trim()) {
          return (
            <div className='flex h-[200px] flex-col items-center justify-center px-4 text-center'>
              <IconSearch size={48} className='mb-4 text-muted-foreground' />
              <h3 className='mb-2 font-semibold'>
                Nenhum resultado encontrado
              </h3>
              <p className='mb-4 text-sm text-muted-foreground'>
                Não encontramos chats que correspondam à sua busca.
              </p>
              <Button
                variant='outline'
                size='sm'
                onClick={() => setSearch('')}
                className='gap-2'
              >
                Limpar busca
              </Button>
            </div>
          )
        }

        const config = emptyStateConfig[tab]
        const EmptyIcon = config.icon

        return (
          <div className='flex h-[200px] flex-col items-center justify-center px-4 text-center'>
            <EmptyIcon size={48} className='mb-4 text-muted-foreground' />
            <h3 className='mb-2 font-semibold'>{config.title}</h3>
            <p className='text-sm text-muted-foreground'>
              {config.description}
            </p>
          </div>
        )
      }

      return tickets.map((ticket, index) => {
        const { id, contact, lastMessage } = ticket
        const isLast = index === tickets.length - 1
        const unreadCount = unreadMessages.get(id) || 0
        const hasUnreadMessages = unreadCount > 0

        return (
          <Fragment key={id}>
            <button
              type='button'
              className={cn(
                `-mx-1 flex w-full rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-secondary/75`,
                currentTicketId === id && 'bg-muted'
              )}
              onClick={() => {
                focusTicket(ticket.id)
              }}
            >
              <div className='flex w-full items-start gap-2'>
                <Avatar className='size-10 shrink-0'>
                  <AvatarImage src={contact.profilePicUrl} alt={contact.name} />
                  <AvatarFallback
                    className={cn(getAvatarColor(contact.name), 'text-white')}
                  >
                    {avatarName(contact.name)}
                  </AvatarFallback>
                </Avatar>
                <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                  <div className='flex items-center'>
                    <span className='truncate font-medium'>
                      {contact.name.replace('@s.whatsapp.net', '')}
                    </span>
                    {unreadCount > 0 && (
                      <Badge className='absolute right-5 bg-accent text-accent-foreground hover:bg-accent/90'>
                        {unreadCount}
                      </Badge>
                    )}
                  </div>
                  <span
                    className={cn(
                      'truncate text-sm',
                      hasUnreadMessages
                        ? 'font-medium text-green-600 dark:text-green-400'
                        : 'text-muted-foreground'
                    )}
                  >
                    {lastMessage?.fromMe && 'Você: '}{' '}
                    {getLastMessageContent(lastMessage)}
                  </span>
                </div>
              </div>
            </button>
            {!isLast && <Separator className='my-1' />}
          </Fragment>
        )
      })
    },
    [currentTicketId, focusTicket, search, setSearch, unreadMessages]
  )

  return (
    <div
      ref={sidebarRef}
      className='flex h-full w-full flex-col gap-2 sm:w-56 lg:w-72 2xl:w-80'
    >
      <div className='sticky top-0 z-10 -mx-4 bg-background px-4 pb-3 shadow-md sm:static sm:z-auto sm:mx-0 sm:p-0 sm:shadow-none'>
        <div className='flex items-center justify-between py-2'>
          <div className='flex gap-2'>
            <h1 className='text-2xl font-bold'>Chat ao vivo</h1>
            <IconMessages size={20} />
          </div>

          <Button
            size='icon'
            variant='ghost'
            className='rounded-lg hover:bg-muted'
            onClick={() => setOpen('new')}
          >
            <IconEdit size={24} className='stroke-muted-foreground' />
          </Button>
        </div>

        <label className='flex h-12 w-full items-center space-x-0 rounded-md border border-input pl-2 focus-within:outline-none focus-within:ring-1 focus-within:ring-ring'>
          <IconSearch size={15} className='mr-2 stroke-slate-500' />
          <span className='sr-only'>Pesquisar</span>
          <input
            type='text'
            className='w-full flex-1 bg-inherit text-sm focus-visible:outline-none'
            placeholder='Pesquisar chat...'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
      </div>

      <Tabs
        defaultValue='atendendo'
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as ChatsTabType)}
        className='w-full'
      >
        <TabsList className='mb-2 grid grid-cols-4'>
          <TabsTrigger value='atendendo'>Atendendo</TabsTrigger>
          <TabsTrigger value='agente'>Agente</TabsTrigger>
          <TabsTrigger value='esperando'>Esperando</TabsTrigger>
          <TabsTrigger value='finalizados'>Finalizados</TabsTrigger>
        </TabsList>

        <TabsContent value='atendendo' className='mt-0'>
          <ScrollArea className='-mx-3 h-[calc(100vh-250px)] [&>div>div]:!block'>
            <div className='p-3'>
              {renderChatList(filterTickets(atendendoChats), 'atendendo')}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value='esperando' className='mt-0'>
          <ScrollArea className='-mx-3 h-[calc(100vh-250px)] [&>div>div]:!block'>
            <div className='p-3'>
              {renderChatList(filterTickets(esperandoChats), 'esperando')}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value='agente' className='mt-0'>
          <ScrollArea className='-mx-3 h-[calc(100vh-250px)] [&>div>div]:!block'>
            <div className='p-3'>
              {renderChatList(filterTickets(agenteChats), 'agente')}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value='finalizados' className='mt-0'>
          <ScrollArea className='-mx-3 h-[calc(100vh-250px)] [&>div>div]:!block'>
            <div className='p-3'>
              {renderChatList(filterTickets(finalizadosChats), 'finalizados')}
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </div>
  )
}
